import axios from "axios";

import { dictionaryApiUrl, projectApiUrl } from "./constants";
import {
  ConsentDetails,
  ConsentProfileState,
  PermissionApplication,
  PermissionGrant,
  consentFieldsToQuery,
  normalizeConsentMissingFields,
} from "./applicationPermissionsModel";

export * from "./applicationPermissionsModel";

// docs/security-and-platform-work-plan.md Этап 7 — the access token is an
// HttpOnly cookie now; these calls all target this app's own backend
// (same-site, withCredentials below), which sends it automatically.
const REQUEST_HEADERS = { "X-Requested-With": "XMLHttpRequest" };

export const fetchPermissionGrants = async (): Promise<PermissionGrant[]> => {
  const response = await axios.get(`${projectApiUrl}/personal_data/permissions`, {
    headers: REQUEST_HEADERS,
    withCredentials: true,
  });
  return Array.isArray(response.data?.grants) ? response.data.grants : [];
};

export const fetchConsentDetails = async (
  requestToken: string,
  language: string
): Promise<ConsentDetails> => {
  const response = await axios.get(`${projectApiUrl}/personal_data/permissions`, {
    params: { request: requestToken, language },
    headers: REQUEST_HEADERS,
    withCredentials: true,
  });
  return response.data as ConsentDetails;
};

export const updatePermissions = async ({
  appId,
  permissions,
  requestToken,
}: {
  appId?: string;
  permissions: string[];
  requestToken?: string;
}) => {
  const response = await axios.put(
    `${projectApiUrl}/personal_data/permissions`,
    {
      appId,
      permissions,
      request: requestToken,
    },
    {
      headers: REQUEST_HEADERS,
      withCredentials: true,
    }
  );
  return response.data;
};

export const fetchConsentMissingFields = async (
  requestedPermissions: string[]
): Promise<ConsentProfileState> => {
  // Query the raw fields behind the request (derived permissions expand to
  // the fields they are computed from) -- never the derived keys themselves.
  const fields = new Set(consentFieldsToQuery(requestedPermissions));
  if (requestedPermissions.some((item) => item.startsWith("data.residence."))) {
    fields.add("data.residence.country");
    fields.add("data.profile.placeOfResidence");
  }
  const queriedFields = [...fields];
  const response = await axios.get(`${projectApiUrl}/personal_data`, {
    params: {
      fields: queriedFields.join(","),
      current_page: 1,
      items_per_page: 1,
      status: "enabled",
    },
    headers: REQUEST_HEADERS,
    withCredentials: true,
  });

  const data = response.data?.data?.[0]?.data || {};
  const legacy = String(data.profile?.placeOfResidence?.value || "");
  return {
    missingFields: normalizeConsentMissingFields(response.data, queriedFields),
    residenceCountry: String(data.residence?.country || legacy.slice(0, 2) || ""),
  };
};

export const saveConsentPersonalData = async (
  payload: Record<string, any>
) => {
  const response = await axios.put(
    `${projectApiUrl}/personal_data`,
    payload,
    {
      headers: REQUEST_HEADERS,
      withCredentials: true,
    }
  );
  return response.data;
};

export const fetchPermissionApplications = async (): Promise<PermissionApplication[]> => {
  const response = await axios.get(dictionaryApiUrl, {
    params: {
      type: "app",
      fields: "_id,key,status,metadata.name,metadata.urlImage,metadata.urlSite,metadata.urlCallback,metadata.permissions",
      items_per_page: 1000,
      action: "GetData",
    },
    headers: { "X-Requested-With": "XMLHttpRequest" },
  });

  return (Array.isArray(response.data?.data) ? response.data.data : [])
    .filter((item: any) => item?.status === "enabled" && item?._id && item?.key)
    .map((item: any) => ({
      appObjectId: String(item._id),
      appId: String(item.key),
      name: String(item.metadata?.name || item.key),
      urlImage: item.metadata?.urlImage,
      urlSite: item.metadata?.urlSite,
      urlCallback: item.metadata?.urlCallback,
      allowedPermissions: Array.isArray(item.metadata?.permissions)
        ? item.metadata.permissions
        : [],
    }));
};
